// One card per sub-feature, each carrying its PRD reference.
//
// Three states, and the difference between the last two is the point. "No waste
// found" is good news; "we could not check for waste" is not. A dashboard that
// shows one as the other lies by omission, so a card that could not run says so
// and cannot be opened.
import React from 'react';
import { FiArrowRight, FiLock } from 'react-icons/fi';
import s from '../AdsHealth.module.css';
import { money, pct } from '../format';

function Body({ card, currency }) {
  if (card.state === 'unavailable') return null;
  switch (card.key) {
    case 'health':
      if (card.state !== 'found') return null;
      return (
        <ul className={s.cardList}>
          <li>
            <span className={s.cardTerm}>Spend in healthy campaigns</span>
            <span className={s.cardFigure}>{pct(card.healthy_spend_share)}</span>
          </li>
          <li>
            <span className={s.cardTerm}>Campaigns scored</span>
            <span className={s.cardFigure}>{card.campaigns_scored}</span>
          </li>
          {card.campaigns_excluded > 0 && (
            <li className={s.cardNote}>
              {card.campaigns_excluded} not yet scored — too little spend, traffic or time.
            </li>
          )}
          {card.capped && (
            <li className={s.cardNote}>Capped by a problem too large to average away.</li>
          )}
        </ul>
      );
    case 'waste':
      if (card.state !== 'found') return null;
      return (
        <ul className={s.cardList}>
          <li>
            <span className={s.cardTerm}>Bought nothing</span>
            <span className={s.cardFigure}>{money(card.no_sale, currency)}</span>
          </li>
          <li>
            <span className={s.cardTerm}>Beyond your target</span>
            <span className={s.cardFigure}>{money(card.over_target, currency)}</span>
          </li>
        </ul>
      );
    case 'readiness':
      if (card.state !== 'found') return null;
      return (
        <ul className={s.cardList}>
          <li>
            <span className={s.cardTerm}>Not ready</span>
            <span className={s.cardFigure}>{card.counts?.['Not ready'] || 0}</span>
          </li>
          <li>
            <span className={s.cardTerm}>At risk</span>
            <span className={s.cardFigure}>{card.counts?.['At risk'] || 0}</span>
          </li>
        </ul>
      );
    default:
      return card.findings ? (
        <ul className={s.cardList}>
          <li>
            <span className={s.cardTerm}>Findings</span>
            <span className={s.cardFigure}>{card.findings}</span>
          </li>
        </ul>
      ) : null;
  }
}

export default function FeatureCards({ data, loading, onOpen }) {
  if (loading && !data) {
    return (
      <div className={s.cardGrid}>
        {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className={s.cardSkeleton} />)}
      </div>
    );
  }
  if (!data?.cards?.length) return null;

  return (
    <div className={s.cardGrid}>
      {data.cards.map((card) => {
        const clickable = card.state !== 'unavailable';
        return (
          <section key={card.key}
                   className={`${s.card} ${s[`card_${card.state}`] || ''} ${clickable ? s.cardOpen : ''}`}
                   onClick={clickable ? () => onOpen(card.tab) : undefined}
                   role={clickable ? 'button' : undefined}
                   tabIndex={clickable ? 0 : undefined}
                   onKeyDown={clickable ? (e) => {
                     if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(card.tab); }
                   } : undefined}>
            <header className={s.cardHead}>
              <span className={s.cardRef}>{card.ref}</span>
              {clickable ? <FiArrowRight className={s.cardGo} /> : <FiLock className={s.cardLock} />}
            </header>
            <h3 className={s.cardTitle}>{card.title}</h3>
            <p className={s.cardQuestion}>{card.question}</p>
            <div className={s.cardHeadline}>
              <span className={s.cardValue}>{card.headline}</span>
            </div>
            <p className={s.cardDetail}>{card.detail}</p>
            <Body card={card} currency={data.currency} />
          </section>
        );
      })}
    </div>
  );
}
